import React, { useCallback, useEffect, useState } from 'react'
import apiClient from '../../services/api'
import { useToast } from '../../context/ToastContext'
import LoadingSpinner from '../../components/LoadingSpinner'

const blogFilters = [
  { key: 'all', label: 'All Posts' },
  { key: 'published', label: 'Published' },
  { key: 'draft', label: 'Drafts' }
]

const initialForm = { title: '', slug: '', excerpt: '', content: '', published: false }

const createSlug = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const AdminBlog = () => {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(initialForm)
  const [editId, setEditId] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const { addToast } = useToast()

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/cms/blog')
      setPosts(res.data)
    } catch (e) { addToast('Error loading blog', 'danger') } finally { setLoading(false) }
  }, [addToast])

  useEffect(() => {
    const initialLoad = async () => {
      try {
        const res = await apiClient.get('/api/cms/blog')
        setPosts(res.data)
      } catch (e) {
        addToast('Error loading blog', 'danger')
      } finally {
        setLoading(false)
      }
    }

    initialLoad()
  }, [addToast])

  const save = async (e) => {
    e.preventDefault()
    try {
      if (editId) {
        await apiClient.put(`/api/cms/blog/${editId}`, form)
      } else {
        await apiClient.post('/api/cms/blog', form)
      }
      addToast('Blog saved', 'success')
      setForm(initialForm)
      setEditId(null)
      load()
    } catch (e) { addToast('Error saving blog post', 'danger') }
  }

  const remove = async (id) => {
    if (!window.confirm('Delete blog post?')) return
    await apiClient.delete(`/api/cms/blog/${id}`)
    addToast('Deleted', 'success')
    load()
  }

  if (loading) return <LoadingSpinner />

  const filteredPosts = posts.filter((post) => {
    if (statusFilter === 'published') return post.published
    if (statusFilter === 'draft') return !post.published
    return true
  })

  const filterCount = (key) => {
    if (key === 'published') return posts.filter((post) => post.published).length
    if (key === 'draft') return posts.filter((post) => !post.published).length
    return posts.length
  }

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h2>Blog</h2>
          <p>
            Manage article drafts and published updates from one place, with auto-generated slugs and clearer status filtering.
          </p>
        </div>
      </div>

      <div className="card p-3 mb-4 shadow-sm admin-form-card">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h5 className="mb-0">{editId ? 'Edit Blog Post' : 'Create Blog Post'}</h5>
          {editId && (
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setForm(initialForm); setEditId(null) }}>
              Cancel Edit
            </button>
          )}
        </div>
        <form onSubmit={save} className="row g-3">
          <div className="col-md-6">
            <input
              className="form-control"
              placeholder="Title"
              value={form.title}
              onChange={(event) => setForm((prev) => ({
                ...prev,
                title: event.target.value,
                slug: editId ? prev.slug : createSlug(event.target.value)
              }))}
              required
            />
          </div>
          <div className="col-md-6">
            <input
              className="form-control"
              placeholder="Slug"
              value={form.slug}
              onChange={(event) => setForm({ ...form, slug: createSlug(event.target.value) })}
              required
            />
          </div>
          <div className="col-12">
            <input
              className="form-control"
              placeholder="Excerpt"
              value={form.excerpt}
              onChange={(event) => setForm({ ...form, excerpt: event.target.value })}
            />
          </div>
          <div className="col-12">
            <textarea
              className="form-control"
              rows="6"
              placeholder="Content"
              value={form.content}
              onChange={(event) => setForm({ ...form, content: event.target.value })}
              required
            />
          </div>
          <div className="col-md-3 d-flex align-items-center">
            <div className="form-check">
              <input type="checkbox" id="published" className="form-check-input" checked={form.published} onChange={(event) => setForm({ ...form, published: event.target.checked })} />
              <label className="form-check-label" htmlFor="published">Published</label>
            </div>
          </div>
          <div className="col-md-3">
            <button className="btn btn-primary w-100">{editId ? 'Update' : 'Add'} Post</button>
          </div>
        </form>
      </div>

      <div className="admin-toolbar">
        <div className="admin-filter-chips">
          {blogFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`admin-filter-chip${statusFilter === filter.key ? ' is-active' : ''}`}
              onClick={() => setStatusFilter(filter.key)}
            >
              {filter.label}
              <span className="admin-filter-chip-count">{filterCount(filter.key)}</span>
            </button>
          ))}
        </div>
        <div className="admin-note">{filteredPosts.length} post{filteredPosts.length === 1 ? '' : 's'} shown</div>
      </div>

      <div className="admin-card-list">
        {filteredPosts.map((post) => (
          <div className="card shadow-sm" key={post._id}>
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                <h5 className="mb-0">{post.title}</h5>
                <span className={`admin-status-badge ${post.published ? 'admin-status-published' : 'admin-status-draft'}`}>
                  {post.published ? 'Published' : 'Draft'}
                </span>
              </div>
              <div className="admin-table-meta mb-2">/{post.slug}</div>
              {post.excerpt && <p className="text-muted mb-2">{post.excerpt}</p>}
              <p className="mb-3">{post.content?.length > 220 ? `${post.content.slice(0, 220)}...` : post.content}</p>
              <div className="admin-action-group">
                <button className="btn btn-sm btn-secondary" onClick={() => { setForm({ title: post.title, slug: post.slug, excerpt: post.excerpt || '', content: post.content || '', published: post.published }); setEditId(post._id) }}>Edit</button>
                <button className="btn btn-sm btn-danger" onClick={() => remove(post._id)}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default AdminBlog
